/**
 * Credit Expiration Utility Functions
 * Handles all credit expiration logic including checking expiration status,
 * formatting dates, and calculating time remaining
 */

/**
 * Check if a credit is expired
 * Uses the backend's hasExpired property if available, otherwise calculates it
 * @param {Object} credit - The credit object
 * @returns {boolean} - True if credit is expired, false otherwise
 */
export const isCreditExpired = (credit) => {
  if (credit?.hasExpired !== undefined) {
    return credit.hasExpired;
  }

  // Fallback: calculate expiration if hasExpired is not available
  if (!credit?.expiresAt) {
    return false; // null = no expiration (backward compatible)
  }

  const expirationDate = new Date(credit.expiresAt);
  const now = new Date();

  return expirationDate < now;
};

/**
 * Check if a credit is expiring soon (within specified days)
 * @param {Object} credit - The credit object
 * @param {number} daysThreshold - Number of days to consider as "expiring soon" (default: 30)
 * @returns {boolean} - True if credit is expiring soon, false otherwise
 */
export const isCreditExpiringSoon = (credit, daysThreshold = 30) => {
  if (!credit?.expiresAt || isCreditExpired(credit)) {
    return false;
  }
  
  const expirationDate = new Date(credit.expiresAt);
  const now = new Date();
  const daysUntilExpiration = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
  
  return daysUntilExpiration <= daysThreshold;
};

/**
 * Get the number of days until credit expires
 * @param {Object} credit - The credit object
 * @returns {number|null} - Number of days until expiration, or null if no expiration
 */
export const getDaysUntilExpiration = (credit) => {
  if (!credit?.expiresAt) {
    return null; // No expiration
  }
  
  const expirationDate = new Date(credit.expiresAt);
  const now = new Date();
  const daysUntilExpiration = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));
  
  return daysUntilExpiration;
};

/**
 * Format expiration date for display
 * @param {Object} credit - The credit object
 * @param {string} locale - Locale for date formatting (default: 'en-US')
 * @returns {string} - Formatted expiration date or "No expiration"
 */
export const formatExpirationDate = (credit, locale = 'en-US') => {
  if (!credit?.expiresAt) {
    return 'No expiration';
  }
  
  const expirationDate = new Date(credit.expiresAt);
  
  return expirationDate.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Get expiration status with color coding
 * @param {Object} credit - The credit object
 * @returns {Object} - Status object with text, color, and badge info
 */
export const getExpirationStatus = (credit) => {
  if (!credit?.expiresAt) {
    return {
      text: 'No expiration',
      color: 'gray',
      badge: null,
      isExpired: false,
      isExpiringSoon: false
    };
  }
  
  if (isCreditExpired(credit)) {
    return {
      text: 'Expired',
      color: 'red',
      badge: 'EXPIRED',
      isExpired: true,
      isExpiringSoon: false
    };
  }
  
  const daysUntilExpiration = getDaysUntilExpiration(credit);
  
  if (daysUntilExpiration <= 7) {
    return {
      text: `Expires in ${daysUntilExpiration} day${daysUntilExpiration === 1 ? '' : 's'}`,
      color: 'red',
      badge: 'EXPIRING SOON',
      isExpired: false,
      isExpiringSoon: true
    };
  }
  
  if (daysUntilExpiration <= 30) {
    return {
      text: `Expires in ${daysUntilExpiration} days`,
      color: 'orange',
      badge: null,
      isExpired: false,
      isExpiringSoon: true
    };
  }
  
  return {
    text: `Expires ${formatExpirationDate(credit)}`,
    color: 'gray',
    badge: null,
    isExpired: false,
    isExpiringSoon: false
  };
};

/**
 * Get CSS classes for expiration status
 * @param {Object} credit - The credit object
 * @returns {string} - Tailwind CSS classes for styling
 */
export const getExpirationClasses = (credit) => {
  const status = getExpirationStatus(credit);
  
  if (status.isExpired) {
    return 'text-pdred bg-pdred/10 border-pdred';
  }
  
  if (status.isExpiringSoon && status.color === 'red') {
    return 'text-pdred bg-pdred/10 border-pdred';
  }
  
  if (status.isExpiringSoon && status.color === 'orange') {
    return 'text-pdorange bg-pdorange/10 border-pdorange';
  }
  
  return 'text-gray-600 bg-gray-50 border-gray-200';
};

